import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import useRateLimitedMutation from '@/app/mutations/RecordingMutation/useRateLimitedMutation';
import { TranscriptionContext, Transcription } from '@/app/mutations/TranscriptionMutation/context';
import fetchTranscription, { FetchTranscriptionParams } from '@/app/mutations/TranscriptionMutation/fetchTranscription';
import { isRateLimitError } from '@/app/utils/errors';
import { useToast } from '@/components/Toast/context';
import { useSentry } from '@/sentry/context';

const ONE_MINUTE_IN_MS = 60 * 1000;

export default function withTranscriptionMutation<T extends object>(
  Component: (props: T) => ReactNode,
) {
  return function ConnectedComponent(props: T) {
    const { hash } = useParams<{ hash: string }>();

    const Sentry = useSentry();
    const toast = useToast();

    const [isTranscribing, setIsTranscribing] = useState(false);

    const { error, mutate, status, variables } = useRateLimitedMutation({
      async mutationFn(params: Omit<FetchTranscriptionParams, 'meeting_id'>) {
        await fetchTranscription({ ...params, meeting_id: hash! });
      },
      onError(error) {
        Sentry.captureException(error);
      },
      retry: false,
    }, ONE_MINUTE_IN_MS);

    const transcription = useMemo<Transcription>(() => ({
      isTranscribing,
      start: () => mutate({ action: 'start' }),
      stop: () => mutate({ action: 'stop' }),
    }), [isTranscribing, mutate]);

    useEffect(function onMutationStatusChange() {
      switch (status) {
        case 'idle':
        case 'pending':
          break;
        case 'success':
          setIsTranscribing(variables?.action === 'start');
          if (variables?.action === 'start') {
            toast.publish({ text: 'Transcription started', variant: 'success' });
          }
          break;
        case 'error':
          if (isRateLimitError(error)) {
            console.warn(error.message);
          } else {
            toast.publish({ text: `Transcription ${variables?.action} failed: Please refresh page.`, variant: 'destructive' });
            if (Sentry) {
              Sentry.captureException(error, {
                tags: {
                  operation: 'transcription',
                  action: variables?.action,
                },
                extra: {
                  meetingId: hash,
                  timestamp: new Date().toISOString(),
                },
              });
            }
          }
          break;
        default: {
          const neverType: never = status;
          throw new Error(`Unhandled status "${neverType}" in withTranscriptionMutation`);
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, status, toast, variables?.action]);

    return (
      <TranscriptionContext.Provider value={transcription}>
        <Component {...props} />
      </TranscriptionContext.Provider>
    );
  };
}
